import React, { useEffect, useState } from 'react';

/**
 * Preloader component for Gastro Bloggers Day
 * Displays a lightweight culinary animation ("Готуємо чудо...")
 * with metallic gold progress bar and percentage counter.
 * Synchronizes with initial data load and 20% video buffering.
 */
export const Preloader = ({ isReady, onComplete }) => {
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (isReady) {
      setFading(true);
      const completeTimer = setTimeout(() => {
        if (onComplete) onComplete();
      }, 400);
      return () => clearTimeout(completeTimer);
    }
  }, [isReady, onComplete]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gradient-to-b from-[#5a1626] via-[#712033] to-[#430f1b] text-white transition-opacity duration-500 ease-out select-none ${
        fading ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      style={{ willChange: 'opacity' }}
    >
      {/* Ambient background glow */}
      <div className="absolute w-72 h-72 rounded-full bg-[#d4af37]/10 blur-3xl pointer-events-none animate-pulse"></div>

      {/* Culinary Animation Container */}
      <div className="relative mb-8 flex flex-col items-center">
        {/* Steam particles */}
        <div className="flex gap-3 mb-2">
          <span className="steam-particle steam-1 text-2xl select-none">♨️</span>
          <span className="steam-particle steam-2 text-3xl select-none">✨</span>
          <span className="steam-particle steam-3 text-2xl select-none">♨️</span>
        </div>

        {/* Simmering Cooking Pot / Pan Vector */}
        <div className="relative w-24 h-24 flex items-center justify-center">
          <svg
            viewBox="0 0 100 100"
            className="w-full h-full drop-shadow-[0_8px_16px_rgba(212,175,55,0.4)] pot-wobble"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Pot Lid / Steam Rim */}
            <ellipse cx="50" cy="38" rx="34" ry="8" fill="url(#goldLidGrad)" />
            {/* Pot Body */}
            <path
              d="M16 38 C16 72 26 84 50 84 C74 84 84 72 84 38 Z"
              fill="url(#potBodyGrad)"
              stroke="#d4af37"
              strokeWidth="2.5"
            />
            {/* Pot Handles */}
            <path
              d="M16 44 C8 44 8 56 16 56"
              stroke="#d4af37"
              strokeWidth="3.5"
              strokeLinecap="round"
            />
            <path
              d="M84 44 C92 44 92 56 84 56"
              stroke="#d4af37"
              strokeWidth="3.5"
              strokeLinecap="round"
            />
            {/* Simmering Bubbles inside rim */}
            <circle cx="38" cy="46" r="3" fill="#fef5c0" opacity="0.85" />
            <circle cx="58" cy="48" r="2.5" fill="#fef5c0" opacity="0.9" />
            <circle cx="48" cy="54" r="3.5" fill="#f9d77e" opacity="0.75" />

            {/* Gradient Definitions */}
            <defs>
              <linearGradient id="goldLidGrad" x1="16" y1="38" x2="84" y2="38" gradientUnits="userSpaceOnUse">
                <stop stopColor="#d4af37" />
                <stop offset="0.5" stopColor="#fef5c0" />
                <stop offset="1" stopColor="#d4af37" />
              </linearGradient>
              <linearGradient id="potBodyGrad" x1="50" y1="38" x2="50" y2="84" gradientUnits="userSpaceOnUse">
                <stop stopColor="#87223b" />
                <stop offset="0.7" stopColor="#5a1626" />
                <stop offset="1" stopColor="#3d0e19" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      {/* Title Text: "Готуємо чудо..." */}
      <h2 className="text-2xl md:text-3xl font-bold font-display gold-gradient-text tracking-wide mb-6 text-center">
        Готуємо чудо...
      </h2>

      {/* Gold Metallic Progress Bar */}
      <div className="w-64 sm:w-80 px-2">
        <div className="w-full h-3.5 bg-black/40 backdrop-blur-md rounded-full p-0.5 border border-[#d4af37]/40 shadow-[0_2px_10px_rgba(0,0,0,0.5)] overflow-hidden">
          <div
            className="h-full rounded-full animate-preloader-progress relative"
            style={{
              background: 'linear-gradient(135deg, #d4af37 0%, #f9d77e 25%, #fef5c0 50%, #f9d77e 75%, #d4af37 100%)',
              backgroundSize: '200% auto',
              boxShadow: '0 0 12px rgba(249, 215, 126, 0.7)'
            }}
          >
            <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
          </div>
        </div>

        {/* Status Text */}
        <div className="flex justify-between items-center mt-3 text-xs md:text-sm font-semibold tracking-wider">
          <span className="text-white/70">Завантаження атмосфери</span>
          <span className="gold-gradient-text font-bold text-sm md:text-base">✨</span>
        </div>
      </div>
    </div>
  );
};
